import { useEffect, useState } from 'react';
import { Browser } from '@capacitor/browser';
import { Link } from 'react-router-dom';
import {
  useMe, useEnrollments, useSubscription, useUpdateProfile, useBillingConfig,
  useReferralInfo, requestAccountDeletion,
} from '../lib/api';
import { useAuth } from '../lib/auth';
import { getThemePreference, setThemePreference, type ThemePreference } from '../lib/theme';
import { AvatarUpload } from '../components/AvatarUpload';
import { VerifiedBadge } from '../components/VerifiedBadge';
import {
  configureRevenueCat,
  getNativeCustomerInfo,
  getNativeOfferings,
  hasActivePro,
  isNativeBillingPlatform,
  onNativeCustomerInfoUpdated,
  packageForPlan,
  presentNativeCustomerCenter,
  purchaseNativePackage,
  restoreNativePurchases,
} from '../lib/native-billing';
import type { BillingConfigResponse, BillingPlan } from '@shared/types';

export function ProfilePage() {
  const { signOut } = useAuth();
  const { data: me } = useMe();
  const { data: enrollments } = useEnrollments();
  const { data: referralData } = useReferralInfo();
  const [showAllReferrals, setShowAllReferrals] = useState(false);

  if (!me) return null;
  const u = me.user;
  const s = me.stats;
  const initials = `${u.firstName[0] ?? ''}${u.lastName[0] ?? ''}`.toUpperCase() || '?';
  const referralPoints = (s as any).referralPoints ?? referralData?.balance ?? 0;
  const referrals = referralData?.referrals ?? [];

  return (
    <div className="space-y-6 max-w-3xl">
      <section className="bg-white rounded-xl border p-6">
        <div className="flex items-center gap-4">
          <AvatarUpload initials={initials} currentKey={u.avatarS3Key} />
          <div className="flex-1">
            <div className="flex items-center gap-1.5">
              <span className="text-xl font-semibold text-squishy-dark">{u.displayName}</span>
              {u.verifiedStudent && <VerifiedBadge size={18} />}
            </div>
            <div className="text-sm text-slate-500">{u.email}</div>
            {u.verifiedStudent && (
              <div className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-squishy-primary">
                <VerifiedBadge size={12} /> Verified Student
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 grid grid-cols-4 gap-4 text-center">
          <Stat label="Level" value={s.level} />
          <Stat label="XP" value={s.totalXp.toLocaleString()} />
          <Stat label="Streak" value={`${s.currentStreak}d`} />
          <Stat label="Referral Pts" value={referralPoints} />
        </div>
      </section>

      <SettingsPanel />

      <AppearancePanel />

      <section className="bg-white rounded-xl border p-6">
        <h2 className="font-semibold mb-3">My Classes</h2>
        {enrollments?.enrollments?.length ? (
          <ul className="space-y-2 text-sm">
            {enrollments.enrollments.map((e: any) => (
              <li key={e.id} className="flex justify-between">
                <Link to={`/class/${e.id}`} className="hover:underline">
                  {e.school_name} · {e.class_code}
                  {e.professor_name ? ` · Prof. ${e.professor_name}` : ' · All teachers'}
                </Link>
                <span className="text-slate-500">{e.term}</span>
              </li>
            ))}
          </ul>
        ) : (
          <Link to="/onboarding" className="text-sm text-squishy-primary">+ Add a class</Link>
        )}
      </section>

      <section className="bg-white rounded-xl border p-6">
        <h2 className="font-semibold mb-3">Referrals</h2>
        {referrals.length > 0 ? (
          <>
            <ul className="space-y-2 text-sm">
              {referrals.slice(0, 2).map((r: any) => (
                <li key={r.id} className="flex items-center justify-between">
                  <span className="text-squishy-dark font-medium">{r.first_name} {r.last_name}</span>
                  <span className="text-xs text-slate-400 capitalize">{r.subscription_tier}</span>
                </li>
              ))}
            </ul>
            {referrals.length > 2 && (
              <button
                onClick={() => setShowAllReferrals(true)}
                className="mt-2 text-xs font-semibold text-squishy-primary hover:underline"
              >
                +{referrals.length - 2} more
              </button>
            )}
          </>
        ) : (
          <p className="text-sm text-slate-500">Share questions to invite friends and earn referral points.</p>
        )}
        {referralData?.referral_url && (
          <button
            onClick={() => { navigator.clipboard.writeText(referralData.referral_url); }}
            className="mt-3 text-xs bg-squishy-primary/10 text-squishy-primary px-3 py-1.5 rounded-lg hover:bg-squishy-primary/20 transition"
          >
            Copy referral link
          </button>
        )}
      </section>

      <SubscriptionPanel />

      <section className="bg-white rounded-xl border p-6">
        <h2 className="font-semibold mb-3">Legal</h2>
        <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
          <Link to="/terms" className="text-slate-500 hover:text-squishy-primary hover:underline">Terms of Service</Link>
          <Link to="/privacy" className="text-slate-500 hover:text-squishy-primary hover:underline">Privacy Policy</Link>
          <Link to="/cookies" className="text-slate-500 hover:text-squishy-primary hover:underline">Cookie Policy</Link>
        </div>
      </section>

      <DeleteAccountSection />

      <div className="flex items-center gap-4 text-sm">
        <button onClick={signOut} className="text-slate-500 hover:text-red-600">Sign out</button>
      </div>

      {showAllReferrals && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end justify-center animate-fade-in" onClick={() => setShowAllReferrals(false)}>
          <div
            className="bg-white rounded-t-2xl w-full max-w-lg max-h-[70vh] overflow-y-auto p-5 pb-8 animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-squishy-dark">All Referrals</h2>
              <button onClick={() => setShowAllReferrals(false)} className="text-slate-400 hover:text-slate-600 text-xl leading-none">&times;</button>
            </div>
            <ul className="space-y-2">
              {[...referrals].sort((a: any, b: any) => (a.first_name + a.last_name).localeCompare(b.first_name + b.last_name)).map((r: any) => (
                <li key={r.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div>
                    <span className="text-sm font-medium text-squishy-dark">{r.first_name} {r.last_name}</span>
                    <span className="text-xs text-slate-400 ml-2">{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                  <span className="text-xs capitalize text-slate-500">{r.subscription_tier}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function SubscriptionPanel() {
  const { data: me } = useMe();
  const { data: subscription } = useSubscription();
  const { data: config } = useBillingConfig();
  const [nativeReady, setNativeReady] = useState(false);
  const [nativeBusy, setNativeBusy] = useState<string | null>(null);
  const [nativeError, setNativeError] = useState<string | null>(null);
  const [nativePackages, setNativePackages] = useState<Record<string, any>>({});
  const [nativeManageUrl, setNativeManageUrl] = useState<string | null>(null);
  const [nativeHasPro, setNativeHasPro] = useState(false);

  const user = me?.user;
  const native = isNativeBillingPlatform();

  useEffect(() => {
    let active = true;
    let unsubscribe: (() => void) | undefined;
    async function loadNativeBilling() {
      if (!native || !user || !config) return;
      setNativeError(null);
      try {
        const configured = await configureRevenueCat(user, config);
        if (!active) return;
        setNativeReady(configured);
        if (!configured) {
          setNativeError('In-app purchases are being configured for this app build.');
          return;
        }
        const [offerings, info] = await Promise.all([
          getNativeOfferings(),
          getNativeCustomerInfo().catch(() => null),
        ]);
        if (!active) return;
        setNativePackages({
          monthly: packageForPlan(offerings, 'monthly'),
          annual: packageForPlan(offerings, 'annual'),
        });
        setNativeManageUrl(info?.managementURL ?? null);
        setNativeHasPro(hasActivePro(info, config.revenueCat.entitlementId));
        unsubscribe = onNativeCustomerInfoUpdated((customerInfo) => {
          setNativeManageUrl(customerInfo.managementURL ?? null);
          setNativeHasPro(hasActivePro(customerInfo, config.revenueCat.entitlementId));
        });
      } catch (err: any) {
        if (!active) return;
        setNativeReady(false);
        setNativeError(err?.message ?? 'Native billing could not be loaded.');
      }
    }
    loadNativeBilling();
    return () => {
      active = false;
      unsubscribe?.();
    };
  }, [native, user, config]);

  if (!user) return null;

  const displayedTier = nativeHasPro ? 'pro' : (subscription?.tier ?? user.subscriptionTier);
  const actualTier = subscription?.actualTier ?? config?.actualTier ?? user.subscriptionTier;

  return (
    <section className="bg-white rounded-xl border p-6 space-y-4">
      <div>
        <h2 className="font-semibold">Subscription</h2>
        <p className="text-sm text-slate-500 mt-1">
          Current plan: <strong className="capitalize text-squishy-dark">{displayedTier}</strong>
          {subscription?.status && <span className="ml-2">({subscription.status})</span>}
        </p>
        <p className="text-xs text-slate-500 mt-1">
          Squishy School Pro is an auto-renewable subscription. New mobile subscribers get a 30-day free trial, then it renews monthly ($4.99 / month) or yearly ($49.99 / year) at the price shown until canceled. Payment is charged to your App Store or Google Play account, and the subscription renews automatically unless canceled at least 24 hours before the end of the current period. Manage or cancel anytime in your app store account settings.
        </p>
        <p className="text-xs text-slate-500 mt-2">
          <Link to="/terms" className="text-squishy-primary hover:underline">Terms of Use (EULA)</Link>
          <span className="mx-1.5 text-slate-300">·</span>
          <Link to="/privacy" className="text-squishy-primary hover:underline">Privacy Policy</Link>
        </p>
      </div>

      {native ? (
        <>
          <div className="rounded-xl border border-squishy-primary/20 bg-squishy-primary/5 p-4 space-y-3">
            <div>
              <div className="font-semibold text-squishy-dark">Squishy School Pro</div>
              <div className="text-sm text-slate-600">Start a 30-day free trial, then choose monthly or yearly in the secure app store checkout.</div>
            </div>
            <button
              className="w-full bg-squishy-primary text-white px-4 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50"
              disabled={!nativeReady || nativeBusy === 'paywall' || (!nativePackages.monthly && !nativePackages.annual)}
              onClick={async () => {
                // Direct StoreKit/Play purchase via the native store sheet (shows price, the
                // 30-day intro trial, and terms). Avoids RevenueCat's hosted paywall, which
                // requires a separately-configured paywall design and otherwise errors.
                const pkg = nativePackages.monthly ?? nativePackages.annual;
                if (!pkg) {
                  setNativeError('Subscriptions are not available right now. Please try again in a moment.');
                  return;
                }
                setNativeBusy('paywall');
                setNativeError(null);
                try {
                  const info = await purchaseNativePackage(pkg);
                  setNativeManageUrl(info.managementURL ?? null);
                  setNativeHasPro(hasActivePro(info, config?.revenueCat.entitlementId ?? 'pro'));
                } catch (err: any) {
                  if (!err?.userCancelled) setNativeError(err?.message ?? 'Purchase did not complete.');
                } finally {
                  setNativeBusy(null);
                }
              }}
            >
              {nativeBusy === 'paywall' ? 'Starting...' : 'Start 30-day free trial'}
            </button>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {(config?.plans ?? fallbackPlans()).map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                nativeReady={nativeReady}
                nativePackage={nativePackages[plan.id]}
                busy={nativeBusy === plan.id}
                onNativePurchase={async () => {
                  const pkg = nativePackages[plan.id];
                  if (!pkg) {
                    setNativeError(`${plan.label} is not available in the store yet.`);
                    return;
                  }
                  setNativeBusy(plan.id);
                  setNativeError(null);
                  try {
                    const info = await purchaseNativePackage(pkg);
                    setNativeManageUrl(info.managementURL ?? null);
                    setNativeHasPro(hasActivePro(info, config?.revenueCat.entitlementId ?? 'pro'));
                  } catch (err: any) {
                    if (!err?.userCancelled) setNativeError(err?.message ?? 'Purchase did not complete.');
                  } finally {
                    setNativeBusy(null);
                  }
                }}
              />
            ))}
          </div>
        </>
      ) : (
        <div className="rounded-xl border border-squishy-primary/20 bg-squishy-primary/5 p-4 text-sm text-slate-600">
          Pro subscriptions are managed through the Squishy School iOS or Android app. Open Squishy School on your phone to start the 30-day free trial, upgrade, restore purchases, or manage your subscription.
        </div>
      )}

      {nativeError && <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">{nativeError}</div>}

      <div className="flex flex-wrap gap-2">
        {native ? (
          <>
            <button
              className="border px-4 py-2 rounded-lg text-sm"
              disabled={!nativeReady || nativeBusy === 'restore'}
              onClick={async () => {
                setNativeBusy('restore');
                setNativeError(null);
                try {
                  const info = await restoreNativePurchases();
                  setNativeManageUrl(info.managementURL ?? null);
                  setNativeHasPro(hasActivePro(info, config?.revenueCat.entitlementId ?? 'pro'));
                } catch (err: any) {
                  setNativeError(err?.message ?? 'Restore did not complete.');
                } finally {
                  setNativeBusy(null);
                }
              }}
            >
              {nativeBusy === 'restore' ? 'Restoring...' : 'Restore purchases'}
            </button>
            <button
              className="border px-4 py-2 rounded-lg text-sm disabled:opacity-50"
              disabled={!nativeManageUrl}
              onClick={() => nativeManageUrl && Browser.open({ url: nativeManageUrl })}
            >
              Manage subscription
            </button>
            <button
              className="border px-4 py-2 rounded-lg text-sm disabled:opacity-50"
              disabled={!nativeReady || nativeBusy === 'customer-center'}
              onClick={async () => {
                setNativeBusy('customer-center');
                setNativeError(null);
                try {
                  const info = await presentNativeCustomerCenter();
                  setNativeManageUrl(info?.managementURL ?? null);
                  setNativeHasPro(hasActivePro(info, config?.revenueCat.entitlementId ?? 'pro'));
                } catch (err: any) {
                  setNativeError(err?.message ?? 'Could not open subscription help.');
                } finally {
                  setNativeBusy(null);
                }
              }}
            >
              {nativeBusy === 'customer-center' ? 'Opening...' : 'Subscription help'}
            </button>
          </>
        ) : actualTier === 'pro' ? (
          <span className="text-sm text-slate-500">Manage this subscription in the mobile app store account that started it.</span>
        ) : null}
      </div>
    </section>
  );
}

function PlanCard({
  plan,
  nativeReady,
  nativePackage,
  busy,
  onNativePurchase,
}: {
  plan: BillingPlan;
  nativeReady: boolean;
  nativePackage: any;
  busy: boolean;
  onNativePurchase: () => Promise<void>;
}) {
  const displayPrice = nativePackage?.product?.priceString ?? plan.price;
  return (
    <div className="border rounded-xl p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-semibold">{plan.label}</div>
          <div className="text-sm text-slate-500">Squishy School Pro</div>
        </div>
        {plan.badge && <span className="text-xs bg-squishy-primary/10 text-squishy-primary px-2 py-1 rounded-full">{plan.badge}</span>}
      </div>
      <div>
        <span className="text-2xl font-bold text-squishy-dark">{displayPrice}</span>
        <span className="text-sm text-slate-500"> / {plan.interval}</span>
        <div className="text-xs text-slate-500 mt-1">30 days free, then auto-renews. Cancel anytime in your app store account.</div>
      </div>
      <button
        className="w-full bg-squishy-primary text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
        disabled={busy || !nativeReady || !nativePackage}
        onClick={onNativePurchase}
      >
        {busy ? 'Working...' : 'Start 30-day free trial'}
      </button>
    </div>
  );
}

function fallbackPlans(): BillingConfigResponse['plans'] {
  return [
    {
      id: 'monthly',
      tier: 'pro',
      interval: 'month',
      label: 'Monthly',
      price: '$4.99',
      priceUsd: 4.99,
      productId: 'pro_monthly',
      stripePriceId: '',
      revenueCatPackageType: 'MONTHLY',
    },
    {
      id: 'annual',
      tier: 'pro',
      interval: 'year',
      label: 'Annual',
      price: '$49.99',
      priceUsd: 49.99,
      productId: 'pro_annual',
      stripePriceId: '',
      revenueCatPackageType: 'ANNUAL',
      badge: 'Best value',
    },
  ];
}

function DeleteAccountSection() {
  const { data: me } = useMe();
  const { signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const email = me?.user.email;
  if (!email) return null;

  return (
    <section className="bg-white rounded-xl border border-red-200 p-6">
      <h2 className="font-semibold text-red-700">Delete account</h2>
      <p className="text-sm text-slate-500 mt-1">
        Permanently delete your Squishy School account and associated data. This cannot be undone.
      </p>
      <button
        onClick={() => { setOpen(true); setError(null); setDone(false); }}
        className="mt-3 border border-red-300 text-red-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-50"
      >
        Delete account
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 animate-fade-in"
          onClick={() => !busy && setOpen(false)}
        >
          <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            {done ? (
              <>
                <h3 className="text-lg font-bold text-squishy-dark">Account deletion requested</h3>
                <p className="text-sm text-slate-600">
                  We have received your request. Your account ({email}) and associated data are scheduled for permanent deletion. You will now be signed out.
                </p>
                <button
                  onClick={() => { void signOut(); }}
                  className="w-full bg-squishy-primary text-white px-4 py-2.5 rounded-lg text-sm font-semibold"
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <h3 className="text-lg font-bold text-red-700">Delete your account?</h3>
                <p className="text-sm text-slate-600">
                  This permanently deletes your account ({email}), study history, and personal data. This action cannot be undone.
                </p>
                <p className="text-xs text-slate-500">
                  Note: an active subscription is billed by Apple or Google and must be canceled separately in your App Store or Google Play account settings.
                </p>
                {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
                <div className="flex gap-2">
                  <button
                    disabled={busy}
                    onClick={() => setOpen(false)}
                    className="flex-1 border px-4 py-2.5 rounded-lg text-sm disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    disabled={busy}
                    onClick={async () => {
                      setBusy(true);
                      setError(null);
                      try {
                        await requestAccountDeletion(email);
                        setDone(true);
                      } catch (e: any) {
                        setError(e?.message ?? 'Could not submit the deletion request. Please try again.');
                      } finally {
                        setBusy(false);
                      }
                    }}
                    className="flex-1 bg-red-600 text-white px-4 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50"
                  >
                    {busy ? 'Deleting...' : 'Delete account'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <div className="text-2xl font-bold text-squishy-dark">{value}</div>
      <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
    </div>
  );
}

function SettingsPanel() {
  const { data: me } = useMe();
  const update = useUpdateProfile();
  const [firstName, setFirstName] = useState(me?.user.firstName ?? '');
  const [lastName, setLastName]   = useState(me?.user.lastName ?? '');
  const [handle, setHandle]       = useState(me?.user.customHandle ?? '');
  const [mode, setMode]           = useState(me?.user.displayNameMode ?? 'real_name');

  if (!me) return null;

  const dirty =
    firstName !== me.user.firstName ||
    lastName  !== me.user.lastName ||
    handle    !== (me.user.customHandle ?? '') ||
    mode      !== me.user.displayNameMode;

  return (
    <section className="bg-white rounded-xl border p-6 space-y-4">
      <h2 className="font-semibold">Settings</h2>

      <div className="grid grid-cols-2 gap-3">
        <Field label="First name">
          <input value={firstName} onChange={(e) => setFirstName(e.target.value)}
                 className="w-full border rounded-lg px-3 py-2 text-sm" />
        </Field>
        <Field label="Last name">
          <input value={lastName} onChange={(e) => setLastName(e.target.value)}
                 className="w-full border rounded-lg px-3 py-2 text-sm" />
        </Field>
      </div>

      <Field label="Display style">
        <div className="flex gap-2 text-sm">
          {(['real_name', 'custom', 'anonymous'] as const).map((m) => (
            <button key={m}
                    onClick={() => setMode(m)}
                    className={`px-3 py-1 rounded-full border ${mode === m ? 'bg-squishy-primary text-white' : ''}`}>
              {m === 'real_name' ? `${me.user.firstName} ${me.user.lastName[0]}.`
                : m === 'custom' ? '@username' : 'Anonymous'}
            </button>
          ))}
        </div>
      </Field>

      {mode === 'custom' && (
        <Field label="Custom username">
          <input value={handle} onChange={(e) => setHandle(e.target.value.toLowerCase())}
                 placeholder="e.g. nurse_sarah"
                 className="w-full border rounded-lg px-3 py-2 text-sm" />
          <p className="text-xs text-slate-500 mt-1">Letters, numbers, underscores, hyphens. 2-50 chars.</p>
        </Field>
      )}

      <button
        disabled={!dirty || update.isPending}
        onClick={() => update.mutate({
          firstName, lastName,
          displayNameMode: mode,
          customHandle: mode === 'custom' ? handle : null,
        } as any)}
        className="bg-squishy-primary text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
      >
        {update.isPending ? 'Saving…' : 'Save changes'}
      </button>
    </section>
  );
}

function AppearancePanel() {
  const [pref, setPref] = useState<ThemePreference>(() => getThemePreference());

  const choose = (p: ThemePreference) => {
    setPref(p);
    setThemePreference(p);
  };

  const options: { value: ThemePreference; label: string; icon: React.ReactNode }[] = [
    {
      value: 'light',
      label: 'Light',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z" />
        </svg>
      ),
    },
    {
      value: 'dark',
      label: 'Dark',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path fillRule="evenodd" d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z" clipRule="evenodd" />
        </svg>
      ),
    },
    {
      value: 'system',
      label: 'System',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path fillRule="evenodd" d="M2.25 5.25a3 3 0 013-3h13.5a3 3 0 013 3V15a3 3 0 01-3 3h-3v.257c0 .597.237 1.17.659 1.591l.621.622a.75.75 0 01-.53 1.28h-9a.75.75 0 01-.53-1.28l.621-.622a2.25 2.25 0 00.659-1.59V18h-3a3 3 0 01-3-3V5.25zm3-1.5a1.5 1.5 0 00-1.5 1.5v9.75c0 .398.158.779.44 1.06.28.282.662.44 1.06.44h13.5a1.5 1.5 0 001.5-1.5V5.25a1.5 1.5 0 00-1.5-1.5H5.25z" clipRule="evenodd" />
        </svg>
      ),
    },
  ];

  return (
    <section className="bg-white rounded-xl border p-6">
      <h2 className="font-semibold">Appearance</h2>
      <p className="text-sm text-slate-500 mt-1">Choose how Squishy School looks. System follows your device setting.</p>
      <div className="mt-3 grid grid-cols-3 gap-2">
        {options.map((o) => (
          <button
            key={o.value}
            onClick={() => choose(o.value)}
            className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 transition-all active:scale-[0.97] ${
              pref === o.value
                ? 'border-squishy-primary bg-teal-50 text-squishy-primary'
                : 'border-slate-200 text-slate-500'
            }`}
          >
            {o.icon}
            <span className="text-xs font-semibold">{o.label}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-slate-500 uppercase tracking-wide">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
